import { useTranslation } from "react-i18next";
import { CreditCard } from "lucide-react";
import { cardLabel, useSaveCard, type BillingStatus } from "../../lib/billing";

/**
 * The card bills are charged to, with "Replace card" (or "Add a card").
 *
 * Shown only to people who can pay, and only when the deployment can save
 * cards at all. The token never reaches the browser — brand, last four and
 * expiry are all the server sends.
 */
export function CardOnFile({ status }: { status: BillingStatus }) {
  const { t } = useTranslation();
  const save = useSaveCard();
  if (!status.can_pay || !status.card_saving) return null;
  const card = status.card;
  const expiry = card && card.expiry_month && card.expiry_year ? `${card.expiry_month}/${card.expiry_year.slice(-2)}` : "";

  return (
    <section className="mt-6" aria-labelledby="card-on-file-heading" data-testid="card-on-file">
      <h2 id="card-on-file-heading" className="text-[12px] font-black uppercase tracking-wider text-muted-foreground mb-3">
        {t("billing.card.title")}
      </h2>
      <div className="flex items-center gap-3 flex-wrap rounded-2xl border border-border px-4 py-3">
        <CreditCard size={18} className="shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          {card ? (
            <>
              <p className="text-[13.5px] font-bold" dir="ltr">
                {expiry ? t("billing.card.onFile", { card: cardLabel(card), expiry }) : cardLabel(card)}
              </p>
              <p className="text-[12px] text-muted-foreground">{t("billing.card.autoCharge")}</p>
            </>
          ) : (
            <p className="text-[13px] text-muted-foreground">{t("billing.card.none")}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="h-9 px-4 rounded-2xl text-[13px] font-bold border border-border hover:bg-muted disabled:opacity-60"
        >
          {save.isPending ? t("billing.card.opening") : card ? t("billing.card.replace") : t("billing.card.add")}
        </button>
      </div>
    </section>
  );
}
